import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';

/**
 * VATFilingDeadlineCard
 * ─────────────────────
 * Proactive ZATCA filing reminder. Fetches /api/vat/filing-deadline,
 * computes urgency tone, and renders a single-row card with two CTAs:
 *
 *   • Deep-link into the VAT Return Lifecycle page (when filing is due)
 *   • "تم تقديمه ✓" button — POST /api/vat/mark-filed (when the
 *     operator already filed via the ZATCA portal directly)
 *
 * Hides itself if the endpoint fails so it never breaks its host page.
 * Tones:
 *   • filed       → emerald  (✓ تم التقديم)
 *   • no_activity → slate    (📭 لا نشاط في هذه الفترة)
 *   • overdue     → rose     (⚠ متأخر)
 *   • critical    → red      (⏰ عاجل ≤ 7 أيام)
 *   • warning     → amber    (⏳ ≤ 14 أيام)
 *   • ok          → sky      (📅 ضمن المدة)
 */
export default function VATFilingDeadlineCard() {
  const [d, setD]           = useState(null);
  const [hidden, setHidden] = useState(false);
  const [marking, setMarking] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/vat/filing-deadline');
      setD(data);
    } catch {
      setHidden(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markFiled = async () => {
    if (!d?.period_name || marking) return;
    const reference = window.prompt(
      `مرجع التقديم على ZATCA (اختياري) — للفترة ${d.period_name}:`,
      ''
    );
    if (reference === null) return;  // operator clicked Cancel
    setMarking(true);
    try {
      await axiosInstance.post('/vat/mark-filed', {
        period_name: d.period_name,
        reference,
      });
      toast.success(`تم تسجيل تقديم ${d.period_name}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل تسجيل التقديم');
    } finally {
      setMarking(false);
    }
  };

  const unmarkFiled = async () => {
    if (!d?.period_name || marking) return;
    if (!window.confirm(`إلغاء تسجيل التقديم اليدوي للفترة ${d.period_name}؟`)) return;
    setMarking(true);
    try {
      await axiosInstance.delete(`/vat/mark-filed/${d.period_name}`);
      toast.success('تم إلغاء التسجيل اليدوي');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل إلغاء التسجيل');
    } finally {
      setMarking(false);
    }
  };

  if (hidden || !d || !d.deadline) return null;

  const TONE = {
    filed:       { bg: 'bg-emerald-50', border: 'border-emerald-300', txt: 'text-emerald-800', label: '✓ تم التقديم' },
    no_activity: { bg: 'bg-slate-50',   border: 'border-slate-300',   txt: 'text-slate-700',   label: '📭 لا نشاط' },
    overdue:     { bg: 'bg-rose-50',    border: 'border-rose-400',    txt: 'text-rose-800',    label: '⚠ متأخر' },
    critical:    { bg: 'bg-red-50',     border: 'border-red-400',     txt: 'text-red-800',     label: '⏰ عاجل' },
    warning:     { bg: 'bg-amber-50',   border: 'border-amber-400',   txt: 'text-amber-800',   label: '⏳ اقتراب الموعد' },
    ok:          { bg: 'bg-sky-50',     border: 'border-sky-300',     txt: 'text-sky-800',     label: '📅 ضمن المدة' },
  };
  const tone = TONE[d.urgency] || TONE.ok;
  const isFiled       = d.urgency === 'filed';
  const isNoActivity  = d.urgency === 'no_activity';
  const isManualFiled = !!d.manual_filed_at;

  const periodLabel = d.period_name?.includes('-Q')
    ? `الربع ${d.period_name.slice(-1)} من سنة ${d.period_name.slice(0, 4)}`
    : `شهر ${d.period_name}`;

  const subtext = isFiled
    ? (isManualFiled
        ? `مودَع يدوياً${d.manual_reference ? ` — مرجع: ${d.manual_reference}` : ''}`
        : 'مودَع داخل النظام')
    : isNoActivity
      ? 'لا توجد فواتير أو مصروفات في هذه الفترة — لا حاجة للتقديم'
      : (d.days_remaining < 0
          ? `متأخر ${Math.abs(d.days_remaining)} يوماً`
          : `${d.days_remaining} يوماً متبقّياً`);

  return (
    <div
      data-testid="vat-filing-deadline-card"
      className={`rounded-2xl border-2 p-5 ${tone.bg} ${tone.border} ${tone.txt} flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4`}
    >
      <div className="flex items-start gap-3">
        <div className="text-3xl">{isNoActivity ? '📭' : isFiled ? '✅' : '📊'}</div>
        <div>
          <div className="text-xs font-bold uppercase opacity-80 tracking-wide">
            موعد تقديم الإقرار الضريبي · ZATCA Filing Deadline
          </div>
          <div className="font-bold text-lg mt-0.5">
            {periodLabel} ({d.period_name})
          </div>
          <div className="text-sm opacity-90 mt-0.5">
            الموعد النهائي: <span className="font-mono font-semibold">{d.deadline}</span>
            {' · '}
            <span className="font-bold">{subtext}</span>
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:items-end gap-2">
        <span
          data-testid="vat-deadline-urgency"
          className={`inline-block px-3 py-1 rounded-full text-xs font-bold border ${tone.border}`}
        >
          {tone.label}
        </span>
        {/* Primary CTA: link into the lifecycle page (hidden once filed) */}
        {!isFiled && !isNoActivity && (
          <a
            href="/vat-returns"
            className="text-xs font-semibold underline hover:opacity-80"
            data-testid="vat-deadline-open-link"
          >
            افتح صفحة VAT Return ←
          </a>
        )}
        {/* Secondary CTA: mark-as-filed (only when NOT filed). Hidden
            on no_activity since there's nothing to mark. */}
        {!isFiled && !isNoActivity && (
          <button
            onClick={markFiled}
            disabled={marking}
            data-testid="vat-mark-filed-btn"
            className="text-[11px] font-semibold px-2 py-1 rounded border border-current hover:bg-white/50 disabled:opacity-50"
          >
            {marking ? '...' : 'تم تقديمه على ZATCA يدوياً ✓'}
          </button>
        )}
        {/* If marked-filed manually, expose an undo */}
        {isFiled && isManualFiled && (
          <button
            onClick={unmarkFiled}
            disabled={marking}
            data-testid="vat-unmark-filed-btn"
            className="text-[11px] font-semibold opacity-70 hover:opacity-100 underline"
          >
            إلغاء التسجيل اليدوي
          </button>
        )}
      </div>
    </div>
  );
}
